import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Crown, Check, Loader2, Zap, Headphones, Brain, Sparkles, Music, Palette } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CRITICAL: every payment call MUST send the auth cookie so the backend can
// identify the logged-in buyer. Without this, `user_id` silently falls back
// to "default" and the paying user's account is never upgraded to premium.
// Applied globally because axios is used throughout the app and cookie-less
// calls never help us anyway (our backend reads session cookies for auth).
axios.defaults.withCredentials = true;

const FEATURES = [
  { icon: Brain, text: 'AI Session Creator — Personalized sessions' },
  { icon: Headphones, text: '8D Spatial Audio — Immersive surround sound' },
  { icon: Music, text: 'Export Audio — Save sessions as WAV files' },
  { icon: Sparkles, text: 'Save Custom Presets — Unlimited presets' },
  { icon: Palette, text: 'Custom Theme Builder — Design your own themes' },
  { icon: Zap, text: 'Full Analytics — Deep session insights' },
];

const UpgradeModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check for payment success on mount (returned from Stripe)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');

    if (payment === 'success' && sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      toast({ title: 'Payment Cancelled', description: 'You can upgrade anytime.', variant: 'destructive' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      try {
        const resp = await axios.get(`${API}/payments/status/${sessionId}`);
        if (resp.data.payment_status === 'paid') {
          setCheckingPayment(false);
          toast({ title: 'Welcome to Premium!', description: 'All features are now unlocked.' });
          onSuccess();
          return;
        }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else {
          setCheckingPayment(false);
          toast({ title: 'Payment Processing', description: 'It may take a moment to confirm. Please refresh.' });
        }
      } catch {
        setCheckingPayment(false);
      }
    };
    poll();
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
      });
      // Redirect to Stripe Checkout
      window.location.href = resp.data.url;
    } catch (error) {
      setLoading(false);
      toast({ title: 'Error', description: 'Could not start checkout. Please try again.', variant: 'destructive' });
    }
  };

  if (checkingPayment) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="upgrade-modal">
        <Card className="w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 border-white/20 text-center p-8">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <h3 className="text-white text-xl font-bold mb-2">Confirming Payment...</h3>
          <p className="text-white/60 text-sm">Please wait while we verify your purchase.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="upgrade-modal">
      <Card className="w-full max-w-lg bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 border-purple-500/30 overflow-hidden">
        <div className="relative">
          <Button onClick={onClose} variant="ghost" size="sm" className="absolute top-4 right-4 text-white hover:bg-white/10 z-10" data-testid="upgrade-close-btn">
            <X className="w-5 h-5" />
          </Button>

          {/* Header */}
          <div className="text-center pt-8 pb-4 px-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-white text-2xl font-bold mb-1" data-testid="upgrade-title">Upgrade to Premium</h2>
            <p className="text-white/60 text-sm">Unlock the full power of NeuroWave</p>
          </div>
        </div>

        <CardContent className="px-6 pb-8">
          {/* Price */}
          <div className="text-center mb-6">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-white text-5xl font-bold">$9.99</span>
              <span className="text-white/50 text-sm">/ lifetime</span>
            </div>
            <p className="text-white/40 text-xs mt-1">One-time payment. No subscriptions.</p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-8">
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`premium-feature-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-purple-400" />
                </div>
                <span className="text-white/80 text-sm">{text}</span>
                <Check className="w-4 h-4 text-green-400 ml-auto flex-shrink-0" />
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-6 text-lg font-semibold rounded-xl"
            data-testid="upgrade-checkout-btn"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting to Checkout...</>
            ) : (
              <><Crown className="w-5 h-5 mr-2" /> Get Premium — $9.99</>
            )}
          </Button>

          <p className="text-white/30 text-xs text-center mt-4">
            Secure checkout powered by Stripe. Instant activation.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpgradeModal;
